import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { 
  Eye, Plus, Edit, Trash2, Download, Upload, Settings, Smartphone, 
  Tablet, Monitor, Palette, Type, Image, Layout, Save, Code, Play, ShieldX, LayoutTemplate
} from "lucide-react";
import { useBlockPages } from "@/hooks/useBlockPages";
import { BlockPageTemplate } from "@/types/blockPage";

export const BlockPageSection = () => {
  const { templates, isLoading } = useBlockPages();
  const [selectedTemplate, setSelectedTemplate] = useState<BlockPageTemplate | null>(null);
  const [activeTab, setActiveTab] = useState("templates");
  const [deviceView, setDeviceView] = useState<'mobile' | 'tablet' | 'desktop'>('desktop');
  const [blockPageSettings, setBlockPageSettings] = useState({
    title: 'Access Restricted',
    message: 'This website has been blocked by your organization\'s internet policy.',
    backgroundColor: '#f8f9fa',
    textColor: '#212529',
    buttonColor: '#007bff',
    logoUrl: '',
    contactInfo: 'If you believe this is an error, please contact your administrator.',
    showRetryButton: true,
    showLogo: false,
    customCSS: ''
  });

  const getDeviceViewportClass = () => {
    switch (deviceView) {
      case 'mobile':
        return 'w-80 h-[640px]';
      case 'tablet':
        return 'w-[768px] h-[1024px]';
      case 'desktop':
        return 'w-full h-[600px]';
      default:
        return 'w-full h-[600px]';
    }
  };

  const generatePreviewHTML = () => {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>${blockPageSettings.title}</title>
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            background: ${blockPageSettings.backgroundColor};
            color: ${blockPageSettings.textColor};
            min-height: 100vh;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 20px;
          }
          .block-container {
            max-width: 600px;
            width: 100%;
            text-align: center;
            background: white;
            border-radius: 12px;
            padding: 40px;
            box-shadow: 0 10px 30px rgba(0,0,0,0.1);
          }
          .logo {
            margin-bottom: 30px;
          }
          .logo img {
            max-width: 120px;
            height: auto;
          }
          .icon {
            width: 80px;
            height: 80px;
            background: #dc3545;
            border-radius: 50%;
            margin: 0 auto 30px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            font-size: 40px;
          }
          h1 {
            font-size: 2rem;
            margin-bottom: 20px;
            color: #dc3545;
            font-weight: 600;
          }
          .message {
            font-size: 1.1rem;
            line-height: 1.6;
            margin-bottom: 30px;
            color: #6c757d;
          }
          .retry-btn {
            background: ${blockPageSettings.buttonColor};
            color: white;
            border: none;
            padding: 12px 30px;
            border-radius: 8px;
            font-size: 1rem;
            cursor: pointer;
            transition: all 0.3s ease;
            margin-right: 10px;
          }
          .retry-btn:hover {
            opacity: 0.9;
            transform: translateY(-1px);
          }
          .contact-btn {
            background: transparent;
            color: ${blockPageSettings.buttonColor};
            border: 2px solid ${blockPageSettings.buttonColor};
            padding: 12px 30px;
            border-radius: 8px;
            font-size: 1rem;
            cursor: pointer;
            transition: all 0.3s ease;
          }
          .contact-btn:hover {
            background: ${blockPageSettings.buttonColor};
            color: white;
          }
          .contact-info {
            margin-top: 30px;
            font-size: 0.9rem;
            color: #6c757d;
            border-top: 1px solid #e9ecef;
            padding-top: 20px;
          }
          @media (max-width: 768px) {
            .block-container {
              padding: 30px 20px;
            }
            h1 {
              font-size: 1.5rem;
            }
            .message {
              font-size: 1rem;
            }
            .retry-btn, .contact-btn {
              display: block;
              width: 100%;
              margin: 10px 0;
            }
          }
          ${blockPageSettings.customCSS}
        </style>
      </head>
      <body>
        <div class="block-container">
          ${blockPageSettings.showLogo && blockPageSettings.logoUrl ? 
            `<div class="logo"><img src="${blockPageSettings.logoUrl}" alt="Logo" /></div>` : 
            '<div class="icon">🚫</div>'
          }
          <h1>${blockPageSettings.title}</h1>
          <div class="message">${blockPageSettings.message}</div>
          <div class="actions">
            ${blockPageSettings.showRetryButton ? '<button class="retry-btn" onclick="history.back()">Go Back</button>' : ''}
            <button class="contact-btn" onclick="alert('Contact administrator')">Request Access</button>
          </div>
          ${blockPageSettings.contactInfo ? `<div class="contact-info">${blockPageSettings.contactInfo}</div>` : ''}
        </div>
      </body>
      </html>
    `;
  };

  const handlePreview = (template: BlockPageTemplate) => {
    setSelectedTemplate(template);
    setActiveTab("preview");
  };

  const handleEdit = (template: BlockPageTemplate) => {
    setSelectedTemplate(template);
    setActiveTab("editor");
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-gray-200 rounded animate-pulse"></div>
        <div className="h-64 bg-gray-200 rounded animate-pulse"></div>
      </div>
    );
  }

  return (
    <div className="px-6 space-y-6">
      <div className="flex items-start">
        <Button onClick={() => setActiveTab("editor")}>
          <Plus className="w-4 h-4 mr-2" />
          New Template
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="templates">Templates</TabsTrigger>
          <TabsTrigger value="editor">Editor</TabsTrigger>
          <TabsTrigger value="preview">Preview</TabsTrigger>
        </TabsList>

        <TabsContent value="templates" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShieldX className="w-5 h-5" />
                Block Page Templates
              </CardTitle>
              <CardDescription>
                Manage your custom block page templates
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Last Modified</TableHead>
                    <TableHead className="w-32">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {templates.map((template) => (
                    <TableRow key={template._id}>
                      <TableCell className="font-medium">{template.name}</TableCell>
                      <TableCell>{template.description}</TableCell>
                      <TableCell>
                        <Badge variant={template.isActive ? "default" : "secondary"}>
                          {template.isActive ? "Active" : "Inactive"}
                        </Badge>
                      </TableCell>
                      <TableCell>{template.lastModified}</TableCell>
                      <TableCell>
                        <div className="flex space-x-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handlePreview(template)}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(template)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="editor" className="space-y-6">
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            {/* Visual Editor Panel */}
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <Palette className="w-5 h-5" />
                    <span>Visual Design</span>
                  </CardTitle>
                  <CardDescription>Customize the appearance of your block page</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Basic Settings */}
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="title">Page Title</Label>
                      <Input
                        id="title"
                        value={blockPageSettings.title}
                        onChange={(e) => setBlockPageSettings(prev => ({ ...prev, title: e.target.value }))}
                        placeholder="Enter page title"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="message">Main Message</Label>
                      <Textarea
                        id="message"
                        value={blockPageSettings.message}
                        onChange={(e) => setBlockPageSettings(prev => ({ ...prev, message: e.target.value }))}
                        placeholder="Enter the main message users will see"
                        rows={3}
                      />
                    </div>
                  </div>

                  <Separator />

                  {/* Color Settings */}
                  <div className="space-y-4">
                    <h4 className="text-sm font-medium flex items-center">
                      <Palette className="w-4 h-4 mr-2" />
                      Colors
                    </h4>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="bg-color">Background Color</Label>
                        <div className="flex space-x-2">
                          <Input
                            id="bg-color"
                            type="color"
                            value={blockPageSettings.backgroundColor}
                            onChange={(e) => setBlockPageSettings(prev => ({ ...prev, backgroundColor: e.target.value }))}
                            className="w-12 h-9"
                          />
                          <Input
                            value={blockPageSettings.backgroundColor}
                            onChange={(e) => setBlockPageSettings(prev => ({ ...prev, backgroundColor: e.target.value }))}
                            placeholder="#f8f9fa"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <Label htmlFor="text-color">Text Color</Label>
                        <div className="flex space-x-2">
                          <Input
                            id="text-color"
                            type="color"
                            value={blockPageSettings.textColor}
                            onChange={(e) => setBlockPageSettings(prev => ({ ...prev, textColor: e.target.value }))}
                            className="w-12 h-9"
                          />
                          <Input
                            value={blockPageSettings.textColor}
                            onChange={(e) => setBlockPageSettings(prev => ({ ...prev, textColor: e.target.value }))}
                            placeholder="#212529"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <Label htmlFor="button-color">Button Color</Label>
                        <div className="flex space-x-2">
                          <Input
                            id="button-color"
                            type="color"
                            value={blockPageSettings.buttonColor}
                            onChange={(e) => setBlockPageSettings(prev => ({ ...prev, buttonColor: e.target.value }))}
                            className="w-12 h-9"
                          />
                          <Input
                            value={blockPageSettings.buttonColor}
                            onChange={(e) => setBlockPageSettings(prev => ({ ...prev, buttonColor: e.target.value }))}
                            placeholder="#007bff"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <Separator />

                  {/* Logo & Contact Settings */}
                  <div className="space-y-4">
                    <h4 className="text-sm font-medium flex items-center">
                      <Image className="w-4 h-4 mr-2" />
                      Logo & Contact
                    </h4>
                    
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="show-logo"
                        checked={blockPageSettings.showLogo}
                        onCheckedChange={(checked) => setBlockPageSettings(prev => ({ ...prev, showLogo: checked }))}
                      />
                      <Label htmlFor="show-logo">Show Logo</Label>
                    </div>
                    
                    {blockPageSettings.showLogo && (
                      <div>
                        <Label htmlFor="logo-url">Logo URL</Label>
                        <Input
                          id="logo-url"
                          value={blockPageSettings.logoUrl}
                          onChange={(e) => setBlockPageSettings(prev => ({ ...prev, logoUrl: e.target.value }))}
                          placeholder="https://example.com/logo.png"
                        />
                      </div>
                    )}
                    
                    <div>
                      <Label htmlFor="contact-info">Contact Information</Label>
                      <Textarea
                        id="contact-info"
                        value={blockPageSettings.contactInfo}
                        onChange={(e) => setBlockPageSettings(prev => ({ ...prev, contactInfo: e.target.value }))}
                        placeholder="Contact information or additional help text"
                        rows={2}
                      />
                    </div>
                  </div>

                  <Separator />

                  {/* Button Settings */}
                  <div className="space-y-4">
                    <h4 className="text-sm font-medium flex items-center">
                      <Layout className="w-4 h-4 mr-2" />
                      Button Options
                    </h4>
                    
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="show-retry"
                        checked={blockPageSettings.showRetryButton}
                        onCheckedChange={(checked) => setBlockPageSettings(prev => ({ ...prev, showRetryButton: checked }))}
                      />
                      <Label htmlFor="show-retry">Show "Go Back" Button</Label>
                    </div>
                  </div>

                  <Separator />

                  {/* Custom CSS */}
                  <div className="space-y-4">
                    <h4 className="text-sm font-medium flex items-center">
                      <Code className="w-4 h-4 mr-2" />
                      Custom CSS
                    </h4>
                    
                    <Textarea
                      value={blockPageSettings.customCSS}
                      onChange={(e) => setBlockPageSettings(prev => ({ ...prev, customCSS: e.target.value }))}
                      placeholder="/* Add custom CSS here */"
                      className="font-mono text-sm"
                      rows={4}
                    />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Live Preview Panel */}
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Eye className="w-5 h-5" />
                      <span>Live Preview</span>
                    </div>
                    {/* Device Toggle Buttons */}
                    <div className="flex items-center space-x-1 bg-gray-100 rounded-lg p-1">
                      <Button
                        variant={deviceView === 'mobile' ? 'default' : 'ghost'}
                        size="sm"
                        onClick={() => setDeviceView('mobile')}
                        className="h-8 px-2"
                      >
                        <Smartphone className="w-4 h-4" />
                      </Button>
                      <Button
                        variant={deviceView === 'tablet' ? 'default' : 'ghost'}
                        size="sm"
                        onClick={() => setDeviceView('tablet')}
                        className="h-8 px-2"
                      >
                        <Tablet className="w-4 h-4" />
                      </Button>
                      <Button
                        variant={deviceView === 'desktop' ? 'default' : 'ghost'}
                        size="sm"
                        onClick={() => setDeviceView('desktop')}
                        className="h-8 px-2"
                      >
                        <Monitor className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardTitle>
                  <CardDescription>
                    See how your block page looks on different devices
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="flex justify-center bg-gray-100 min-h-[500px] p-8">
                    <div 
                      className={`${getDeviceViewportClass()} bg-white rounded-lg shadow-lg overflow-hidden transition-all duration-300`}
                      style={{ transform: deviceView === 'mobile' ? 'scale(0.8)' : 'scale(1)' }}
                    >
                      <iframe
                        srcDoc={generatePreviewHTML()}
                        className="w-full h-full border-0"
                        title="Block Page Preview"
                        sandbox="allow-same-origin"
                      />
                    </div>
                  </div>
                  
                  <div className="flex justify-between items-center p-4 border-t bg-gray-50">
                    <div className="text-sm text-gray-600">
                      Viewing: {deviceView === 'mobile' ? 'Mobile (320px)' : deviceView === 'tablet' ? 'Tablet (768px)' : 'Desktop (1200px+)'}
                    </div>
                    <div className="flex space-x-2">
                      <Button variant="outline" size="sm" onClick={() => {
                        const htmlContent = generatePreviewHTML();
                        const blob = new Blob([htmlContent], { type: 'text/html' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = 'block-page.html';
                        a.click();
                        URL.revokeObjectURL(url);
                      }}>
                        <Download className="w-4 h-4 mr-2" />
                        Export HTML
                      </Button>
                      <Button size="sm">
                        <Save className="w-4 h-4 mr-2" />
                        Save Template
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="preview" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <LayoutTemplate className="w-5 h-5" />
                Template Preview
              </CardTitle>
              <CardDescription>
                {selectedTemplate ? `Previewing: ${selectedTemplate.name}` : "Select a template to preview"}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {selectedTemplate ? (
                <div className="border rounded-lg bg-white">
                  <div 
                    className="min-h-96 p-8"
                    dangerouslySetInnerHTML={{
                      __html: `<style>${selectedTemplate.cssContent}</style>${selectedTemplate.htmlContent}`
                    }}
                  />
                </div>
              ) : (
                <div className="text-center py-12">
                  <Eye className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">No template selected for preview</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};